import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import React, { useCallback, useEffect, useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { FormProvider, useForm } from 'react-hook-form';
import {
  FormInformativePromotionCodeInput,
  FormInputHidden,
  ReactFormPromotionCodeInput,
  ReactFormSelect,
} from '../../../../components';
import CheckoutTotalField from './CheckoutTotalField/CheckoutTotalField';
import useStyles from '../styles';
import { selectCurrentUser } from '../../../../store/modules/users/selectors';
import PromoCodeValidateButton from './PromoCodeValidateButton';
import CheckoutFormSubmitButton from './CheckoutFormSubmitButton';
import {
  selectAvailableToUpgradeProductList,
  selectProductList, selectProductOptions,
} from '../../../../store/modules/products/selectors';
import { getProductListRequest } from '../../../../store/modules/products/actions';
import yup from '../../../../utility/yup';
import BillingService from '../../../../services/BillingService';
import { ProductCodes } from '../../../../types/products';
import CheckoutFormModal from './CheckoutFormModal';
import { selectPaymentMethod } from '../../../../store/modules/billing/selectors';
import CheckoutCardFields from './CheckoutCardFields';
import { yupResolver } from '@hookform/resolvers/yup';

const checkoutSchema = yup.object().shape({
  promo_code: yup.string().transform((value) => (!value ? undefined : value))
    .min(8).max(12)
    .matches(
      /^.[a-zA-Z0-9]+$/,
      {
        message: 'Only alphanumeric characters',
        excludeEmptyString: true,
      },
    )
    .trim()
    .label('Code'),
  zip_code: yup.string().required(),
  product_code: yup.string().required(),
});

type Props = {
  productCode?: ProductCodes;
};

const CheckoutForm = ({ productCode }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { data: user } = useSelector(selectCurrentUser);
  const { data: fullProductList } = useSelector(selectProductList);
  const subscriptionList = useSelector(selectAvailableToUpgradeProductList);
  const subscriptionOptions = useSelector(selectProductOptions);
  const customerFullName = user?.ref.full_name_first_last || '';
  const stripe = useStripe();
  const elements = useElements();
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activePromoCode = user?.ref?.active_subscription?.promotion_code;
  const currentCode = activePromoCode && activePromoCode.code;
  const defaultProductCode = (subscriptionList && subscriptionList.length) ? subscriptionList[0].product_code : '';
  const currentProductCode = productCode || defaultProductCode;
  const { data: paymentMethod } = useSelector(selectPaymentMethod);
  const [validPaymentCard, setValidPaymentCard] = useState(false);
  const [useSavedCard, setUseSavedCard] = useState(Boolean(paymentMethod && paymentMethod.valid_method_found));

  const initialValues = {
    product_code: currentProductCode,
    promo_code: currentCode || '',
    zip_code: '',
  };

  useEffect(() => {
    if (!fullProductList) {
      dispatch(getProductListRequest());
    }
  }, [dispatch, fullProductList]);

  const form = useForm({
    defaultValues: initialValues || {},
    resolver: yupResolver(checkoutSchema),
    mode: 'onChange',
  });
  const { getValues, reset } = form;

  useEffect(() => {
    if (currentProductCode && !getValues().product_code) {
      reset(initialValues);
    }
  }, [getValues, currentProductCode, reset, initialValues]);

  const handleSubmit = useCallback(async (submitEvent) => {
    submitEvent.preventDefault();
    setIsSubmitting(true);
    const formValues = getValues();
    const cardElement = elements?.getElement(CardNumberElement);
    let paymentMethodId = paymentMethod ? paymentMethod.payment_method_id : '';
    if (!useSavedCard) {
      if (!stripe || !elements || !cardElement) {
        return;
      }

      // Create stripe payment
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: customerFullName,
          address: {
            postal_code: formValues.zip_code as string,
          },
        },
      });

      // Handler stripe error
      if (result.error) {
        setCheckoutError(result.error.message || '');
        setSuccess(false);
        setOpen(true);
        setIsSubmitting(false);
        console.log('[error]', result.error.message);
        return;
      }

      paymentMethodId = result.paymentMethod ? result.paymentMethod.id : '';
    }

    try {
      // Create checkout
      const checkoutInfo = await BillingService.checkout({
        user_id: user?.id || -1,
        product_code: formValues.product_code as string,
        promotion_code: formValues.promo_code as string,
        payment_method_id: paymentMethodId,
      });

      setSuccess(!(checkoutInfo && checkoutInfo.error));
      setOpen(true);
    } catch (e) {
      // Handle checkout error
      setCheckoutError(e?.response.data.error);
      setSuccess(false);
      setOpen(true);
    }

    setIsSubmitting(false);
  }, [
    stripe,
    elements,
    customerFullName,
    user,
    setCheckoutError,
    setIsSubmitting,
    getValues,
    useSavedCard,
    paymentMethod,
  ]);

  return (
    <>
      <FormProvider
        {...form}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ReactFormSelect
                items={subscriptionOptions}
                label="Plan"
                name="product_code"
              />
            </Grid>
            <Grid item xs={12}>
              {
                activePromoCode
                  ? (
                    <>
                      <FormInformativePromotionCodeInput
                        label="Promo Code"
                        promotionCode={activePromoCode}
                      />
                      <FormInputHidden name="promo_code" />
                    </>
                  )
                  : (
                    <>
                      <ReactFormPromotionCodeInput
                        label="Promo Code"
                        name="promo_code"
                      />
                      <Box mt={1}>
                        <PromoCodeValidateButton />
                      </Box>
                    </>
                  )
              }
            </Grid>
            <CheckoutCardFields
              useSavedCard={useSavedCard}
              setUseSavedCard={setUseSavedCard}
              paymentMethod={paymentMethod}
              updateValidState={setValidPaymentCard}
            />
            <hr className={classes.breakLine} />
            <Grid item xs={12}>
              {subscriptionOptions && subscriptionOptions.length > 0
              && <CheckoutTotalField appliedPromoCode={activePromoCode} />}
            </Grid>
            <Grid item xs={12}>
              <CheckoutFormSubmitButton
                disabled={isSubmitting}
                validPaymentData={validPaymentCard}
                useSavedCard={useSavedCard}
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
      <CheckoutFormModal
        open={open}
        setOpen={setOpen}
        success={success}
        errorMessage={checkoutError}
      />
    </>
  );
};

export default CheckoutForm;
